/* eslint-disable no-restricted-imports */
/* eslint-disable complexity */
/* eslint-disable react/jsx-no-comment-textnodes */
/* eslint-disable react/jsx-no-literals */
/* eslint-disable react/jsx-no-leaked-render */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import React, { FC, useEffect, useMemo } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogContent,
	Grid,
	Stack,
	Typography,
	useMediaQuery,
} from '@mui/material';
import { useLocalization } from '@/data/Localization';
import { AddressCard } from '@/components/blocks/AddressCard';
import { ContentProvider } from '@/data/context/content';
import { useAddressBook } from '@/data/Content/AddressBook';
import { ADDRESS_INIT, makeEditable } from '@/utils/address';
import { AddressModify } from '@/components/content/AddressBook/Modify';
import { ID } from '@/data/types/Basic';
import AddIcon from '@mui/icons-material/Add';
import { shippingTitleGeneralSX } from '@/components/content/CheckOut/parts/Shipping/custom/styles/typography';
import {
	addAddressButton,
	containerAddressook,
	addNotAddressButton,
	AddreessModalFrameSX,
} from '@/components/content/Account/styles/addressBook';
import { AddressBookSkeleton } from '@/components/content/Account/parts/Skeletons/AddressBookSkeleton';
import { useTheme } from '@mui/material/styles';
import {
	NotificationsModalHeaderFrameSX,
	NotificationsModalHeaderIconSX,
} from '@/components/content/Notifications/styles/modal';
import { ClearIcon } from '@mui/x-date-pickers';
import getValueByPathFromUnknown from 'integration/utils/getValueByPathFromUnknown';
import { valueIsArrayOfString } from 'integration/utils/typeAssertions/valueIsArrayOfString';
import valueIsString from 'integration/utils/typeAssertions/valueIsString';

export const AddressBook: FC<{ id: ID }> = () => {
	const addrBook = useAddressBook();
	const {
		displayAddresses,
		addresses,
		getCardActions,
		editableAddress,
		setEditableAddress,
		onCreateOrEdit,
	} = addrBook;
	const localization = useLocalization('AddressBook');

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const primaryPersonalData = useMemo(() => {
		const primaryAddress = addresses.find(({ primary }) => primary === 'true');
		return primaryAddress ? primaryAddress : addresses[0];
	}, [addresses]);

	const primaryAddressPersonalData = {
		email1: primaryPersonalData?.email1 ?? '',
		firstName: primaryPersonalData?.firstName ?? '',
		lastName: primaryPersonalData?.lastName ?? '',
		xaddr_lastNameMat: primaryPersonalData?.attributes?.[0]?.contactInfoAttrValue ?? '',
		phone1: primaryPersonalData?.phone1 ?? '',
	};

	const handleClose = () => {
		setEditableAddress(undefined);
	};

	useEffect(() => {
		if (primaryPersonalData && !primaryPersonalData.zipCode) {
			onCreateOrEdit(makeEditable(primaryPersonalData))();
		}
	}, [primaryPersonalData, onCreateOrEdit]);

	if (!displayAddresses) {
		return <AddressBookSkeleton />;
	}

	const keyToValue = ['addressLine', 'city', 'country', 'email1', 'nickName', 'state', 'zipCode'];
	const primaryAddress = addresses.find((address) => address.primary === 'true');
	const index = addresses.findIndex((address) => address.primary === 'true');

	const indexMissing = primaryAddress ? { primaryAddress, index } : null;

	const hasMissingValue = keyToValue.map((key) => {
		if (!primaryAddress) {
			return false;
		}

		const primaryAddressByKey = getValueByPathFromUnknown(primaryAddress, [key]);

		if (valueIsArrayOfString(primaryAddressByKey)) {
			const missing = primaryAddressByKey.map((add: string, index: number) => {
				if (index < 2) {
					return add === '' || add === 'Sin dirección';
				}
				return false;
			});

			return missing.some((missing) => missing === true);
		}

		return !valueIsString(primaryAddressByKey) || primaryAddressByKey === '';
	});

	const someHasMissingValue = hasMissingValue.some((value) => value === true);

	return (
		<>
			{editableAddress && (
				<ContentProvider value={{ ...addrBook }}>
					<Dialog
						PaperProps={{ sx: AddreessModalFrameSX(isMobile, theme) }}
						open={true}
						onClose={handleClose}
						fullWidth
						maxWidth="sm"
					>
						<Box sx={NotificationsModalHeaderFrameSX(isMobile)}>
							<ClearIcon sx={NotificationsModalHeaderIconSX} onClick={handleClose} />
						</Box>
						<DialogContent>
							<AddressModify addressBook={true} />
						</DialogContent>
					</Dialog>
				</ContentProvider>
			)}

			<Stack spacing={1} sx={containerAddressook}>
				<Typography component={'h1'} sx={shippingTitleGeneralSX}>
					{'TUS DIRECCIONES'}
				</Typography>
				{isMobile && (
					<Button
						sx={{
							alignSelf: 'flex-start',
							padding: '0px',
							'&:hover': {
								backgroundColor: 'transparent',
							},
							marginTop: 'var(--spacing-5) !important',
						}}
						id="checkout-new-address-button"
						data-testid="checkout-new-address-button"
						color="secondary"
						variant="text"
						onClick={
							primaryPersonalData && primaryPersonalData.zipCode
								? onCreateOrEdit({ ...ADDRESS_INIT, ...primaryAddressPersonalData })
								: onCreateOrEdit(makeEditable(primaryPersonalData as any))
						}
					>
						<Typography fontSize={'var(--font-size-sm)'}>{'+ Agregar dirección'}</Typography>
					</Button>
				)}

				{!addresses?.length ? (
					<>
						<Typography variant="subtitle2">{localization.NoAddrMsg.t()}</Typography>
						<Grid item>
							<Button
								sx={addNotAddressButton}
								variant="contained"
								data-testid="address-book-add"
								id="address-book-add"
								onClick={onCreateOrEdit({ ...ADDRESS_INIT, ...primaryAddressPersonalData })}
							>
								<AddIcon sx={{ fontSize: '50px' }} />
								<Typography fontWeight={600} fontSize={'var(--font-size-sm)'}>
									{'Agregar dirección'}
								</Typography>
							</Button>
						</Grid>
					</>
				) : !displayAddresses?.length ? (
					<Typography variant="subtitle2">{localization.NoAddrForFilterMsg.t()}</Typography>
				) : (
					<Stack
						display={'flex'}
						flexDirection={'row'}
						marginTop={!isMobile ? 'var(--spacing-5) !important' : ''}
					>
						{primaryPersonalData && primaryPersonalData.zipCode ? (
							<Grid container={!isMobile} spacing={2} alignItems="stretch" md={9}>
								{displayAddresses.map((address) => (
									<Grid paddingTop={'var(--spacing-5)'} xs={12} sm={4} item key={address.nickName}>
										<AddressCard
											showType={true}
											address={address}
											actions={getCardActions(makeEditable(address))}
											indexMissing={someHasMissingValue ? indexMissing?.index : undefined}
										/>
									</Grid>
								))}
								{!isMobile && (
									<Grid paddingTop={'var(--spacing-5)'} xs={12} sm={4} item>
										<Button
											sx={addAddressButton}
											variant="contained"
											data-testid="address-book-add"
											id="address-book-add"
											onClick={onCreateOrEdit({ ...ADDRESS_INIT, ...primaryAddressPersonalData })}
										>
											<AddIcon sx={{ fontSize: '50px' }} />
											<Typography fontWeight={600} fontSize={'var(--font-size-sm)'}>
												{'Agregar dirección'}
											</Typography>
										</Button>
									</Grid>
								)}
							</Grid>
						) : (
							<Grid container={!isMobile} spacing={2} alignItems="stretch" md={9}>
								{!isMobile && (
									<Grid paddingTop={'var(--spacing-5)'} xs={12} sm={4} item>
										<Button
											sx={addAddressButton}
											variant="contained"
											data-testid="address-book-add"
											id="address-book-add"
											onClick={onCreateOrEdit(makeEditable(primaryPersonalData as any))}
										>
											<AddIcon sx={{ fontSize: '50px' }} />
											<Typography fontWeight={600} fontSize={'var(--font-size-sm)'}>
												{'Agregar dirección'}
											</Typography>
										</Button>
									</Grid>
								)}
							</Grid>
						)}
					</Stack>
				)}
			</Stack>
		</>
	);
};
